import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import { Icon } from '../../components/IconWrapper';
import { mdiCheckCircle, mdiCircle, mdiClockOutline, mdiLoading, mdiMagnify, mdiPen, mdiPlus, mdiTrashCan, mdiAlertCircle, mdiCancel, mdiFilterOutline, mdiSwapHorizontal, mdiPlay, mdiAccountAlert, mdiAccountPlus, mdiAccountStar, mdiAccountSwitch, mdiDeleteCircle } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import { FormTicket } from './FormTicket';
import Grid, { GridItem } from '@/components/layout/Grid';
import { useAuth } from '@/lib/useAuth';
import { queryClient } from '@/queryClient';
import { format } from 'date-fns';
import { TicketStatus, TicketPrioridade, TicketDto } from '@tera/shared/src/dto/TicketDto';
import axios from 'axios';
import { ModalTransferirTicket, TransferenciaTicketData } from './ModalTransferirTicket';
import { TicketDetalhe } from './TicketDetalhe';
import { hasAuthorization, rolesManager } from '@tera/shared/src/lib/auth';
import { Link } from 'react-router-dom';
import Badge from '@/components/layout/Badge';

type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

type TicketPageProps = {};

const TicketPage = ({ }: TicketPageProps) => {
    const searchParams = new URLSearchParams(window.location.search);

    const [selectedTickets, setSelectedTickets] = React.useState<number[]>(() => {
        const param = searchParams.get('id');
        if (param) {
            return param.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        }
        return [];
    });

    const [editObject, setEditObject] = React.useState<any>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<
        string,
        string
    > | null>(null);

    const { user } = useAuth();

    const [search, setSearch] = React.useState<string>('');
    const debouncedSearch = useDebounce(search, 500);

    // Status filter
    const [statusFilter, setStatusFilter] = React.useState<string[]>(['aberto', 'em_progresso']);
    const [meusTickets, setMeusTickets] = React.useState<boolean>(false);

    // Departamento filter
    const [departamentoFilter, setDepartamentoFilter] = React.useState<number | null>(null);
    const [showFilters, setShowFilters] = React.useState<boolean>(false);
    const [showTransferirModal, setShowTransferirModal] = React.useState<boolean>(false);
    const [ticketParaTransferir, setTicketParaTransferir] = React.useState<any>(null);
    const [ticketDetalheId, setTicketDetalheId] = React.useState<number | null>(null);

    // Fetch groups for filter
    const gruposQuery = useQuery(['usuario_grupo/box'], async () => {
        const { data } = await axios.get('/api/usuario_grupo/box', {
            params: { take: 100 },
            withCredentials: true,
        });
        return data.result;
    });

    const getStatusField = (ticket: TicketDto): JSX.Element => {
        const statusColors: Record<TicketStatus, string> = {
            aberto: 'var(--tc-color-warning)',
            em_progresso: 'var(--tc-color-primary)',
            finalizado: 'var(--tc-color-success)',
            cancelado: 'var(--tc-color-danger)',
        };
        const statusLabels: Record<TicketStatus, string> = {
            aberto: 'Não iniciado',
            em_progresso: 'Em Progresso',
            finalizado: 'Finalizado',
            cancelado: 'Cancelado',
        };
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: statusColors[ticket.status] || 'var(--tc-color-gray-700)'
            }}>
                {getTicketIcon(ticket)}
                {statusLabels[ticket.status] || 'N/A'}
            </div>
        );
    }

    const getTicketIcon = (ticket: TicketDto) => {
        switch (ticket.status) {
            case 'aberto':
                return <span title='Não iniciado. Visualizar para inicar atendimento' className="notif notif-warning"><Icon path={mdiCircle} size={1} color="var(--tc-color-warning)" /></span>;
            case 'em_progresso':
                return <Icon path={mdiClockOutline} size={1} color="var(--tc-color-primary)" title='Em progresso' />;
            case 'finalizado':
                return <Icon path={mdiCheckCircle} size={1} color="var(--tc-color-success)" title='Finalizado' />;
            case 'cancelado':
                return <Icon path={mdiCancel} size={1} color="var(--tc-color-danger)" title='Cancelado' />;
            default:
                return <Icon path={mdiCircle} size={1} color="var(--tc-color-gray-500)" title='N/A' />;
        }
    }

    const priorityElem: Record<TicketPrioridade, JSX.Element> = {
        alta: <span><Icon path={mdiAlertCircle} size={0.8} color="var(--tc-color-danger)" /> Alta</span>,
        media: <span><Icon path={mdiClockOutline} size={0.8} color="var(--tc-color-gray-700)" /> Média</span>,
        baixa: <span><Icon path={mdiCheckCircle} size={0.8} color="var(--tc-color-success)" /> Baixa</span>,
    };

    const isSLAOverdue = (ticket: any) => {
        if (!ticket.sla_deadline) return false;
        return new Date(ticket.sla_deadline) < new Date();
    };

    const headers: DataTableHeader[] = [
        {
            label: '#',
            width: '80px',
            column: 'id',
        },
        {
            label: 'Título',
            width: '30%',
            column: 'titulo',
            render: (row: any) => {
                const slaDate = new Date(row.sla_deadline);
                const isAboutToExpire = slaDate.getTime() - Date.now() < 2 * 60 * 60 * 1000; // 2 hours
                const isOverdue = isSLAOverdue(row);

                return (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {(!Boolean(row.usuario_responsavel_id)) && (
                                <Icon path={mdiAccountAlert} size={1} color="var(--tc-color-warning)" title='Sem atendente associado' />
                            )}
                            <span>{row.titulo}</span>
                        </div>

                        {isAboutToExpire && !isOverdue && row.status !== 'finalizado' && row.status !== 'cancelado' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--tc-color-warning)', fontSize: '0.8rem', marginTop: '4px' }}>
                                <Icon path={mdiAlertCircle} size={0.6} color="var(--tc-color-warning)" />
                                SLA próximo de vencer
                            </div>
                        )}
                        {isOverdue && row.status !== 'finalizado' && row.status !== 'cancelado' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--tc-color-danger)', fontSize: '0.8rem', marginTop: '4px' }}>
                                <Icon path={mdiAlertCircle} size={0.6} color="var(--tc-color-danger)" />
                                SLA vencido
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            label: 'Cliente',
            width: '20%',
            render: (row: any): string => row.cliente?.nome ?? '-',
        },
        {
            label: 'Status',
            width: '15%',
            render: (row: any) => {
                return getStatusField(row);
            },
        },
        {
            label: 'Responsável',
            width: '15%',
            render: (row: any): string => {
                if (!row.usuario_responsavel && !row.grupo_responsavel) {
                    return '-';
                }
                if (row.usuario_responsavel) {
                    const isYou = row.usuario_responsavel.id === user.id;
                    return row.usuario_responsavel.nome + (isYou ? ' (Você)' : '');
                }

                return row.grupo_responsavel.nome + ' (Grupo)';
            },
        },
        // {
        //     label: 'Status',
        //     width: '15%',
        //     render: (row: any) => (
        //         <div style={{
        //             display: 'flex',
        //             alignItems: 'center',
        //             gap: '4px',
        //             color: getStatusColor(row.status)
        //         }}>
        //             <Icon path={getStatusIcon(row.status)} size={0.7} color={getStatusColor(row.status)} />
        //             {formatStatus(row.status)}
        //         </div>
        //     ),
        // },
        {
            label: 'Prioridade',
            width: '10%',
            render: (row: any) => (
                priorityElem[(row.prioridade ?? 'media') as TicketPrioridade]
            ),
        },
        {
            label: 'Criado em',
            width: '15%',
            render: (row: any) => row.data_criacao ? format(new Date(row.data_criacao), 'dd/MM/yyyy HH:mm') : null,
        },
        {
            label: 'SLA',
            width: '10%',
            render: (row: any) => {
                if (!row.sla_deadline) return '-';
                const slaDate = new Date(row.sla_deadline);
                const isAboutToExpire = slaDate.getTime() - Date.now() < 2 * 60 * 60 * 1000; // 2 hours
                const isOverdue = slaDate < new Date();
                const color = isOverdue ? 'var(--tc-color-danger)' : isAboutToExpire ? 'var(--tc-color-warning)' : 'var(--tc-color-gray-700)';
                const title = isOverdue ? 'SLA vencido' : isAboutToExpire ? 'SLA prestes a vencer' : 'SLA dentro do prazo';
                return (
                    <div style={{ color, whiteSpace: 'nowrap' }} title={title}>
                        {isAboutToExpire && !isOverdue && <Icon path={mdiAlertCircle} size={0.8} color="var(--tc-color-warning)" />}
                        {isOverdue && <Icon path={mdiAlertCircle} size={0.8} color="var(--tc-color-danger)" />}
                        {format(slaDate, 'dd/MM/yyyy HH:mm')}
                    </div>
                );
            }
        },
        {
            label: 'Ações',
            width: '100px',
            stickRight: true,
            alignRight: true,
            render: (row: any) => {
                return (
                    <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            variant="none-secondary"
                            icon={mdiMagnify}
                            title="Visualizar"
                            onClick={() => setTicketDetalheId(row.id)}
                        />
                        <Botao
                            variant="none-info"
                            icon={mdiAccountStar}
                            title={
                                (!hasAuthorization(user, rolesManager) && !user.grupos.find((it: any) => it.id === row.grupo_responsavel_id))
                                    ? '(Sem permissão)'
                                    : row.usuario_responsavel_id === user.id
                                        ? '(Este ticket já é seu)'
                                        : row.status === 'finalizado' || row.status === 'cancelado'
                                            ? '(Ticket finalizado ou cancelado)'
                                            : 'Assumir ticket'
                            }
                            onClick={() => handleAssumirTicket(row)}
                            disabled={
                                (!hasAuthorization(user, rolesManager) && !user.grupos.find((it: any) => it.id === row.grupo_responsavel_id)) ||
                                row.usuario_responsavel_id === user.id ||
                                row.status === 'finalizado' ||
                                row.status === 'cancelado'
                            }
                        />
                        <Botao
                            variant="none-info"
                            icon={mdiAccountSwitch}
                            title={
                                (!hasAuthorization(user, rolesManager) && !user.grupos.find((it: any) => it.id === row.grupo_responsavel_id))
                                    ? '(Sem permissão)'
                                    : row.status === 'finalizado' || row.status === 'cancelado'
                                        ? '(Ticket finalizado ou cancelado)'
                                        : 'Transferir'}
                            disabled={
                                (!hasAuthorization(user, rolesManager) && !user.grupos.find((it: any) => it.id === row.grupo_responsavel_id)) ||
                                row.status === 'finalizado' ||
                                row.status === 'cancelado'
                            }
                            onClick={() => handleTransferir(row)}
                        />
                        {/*
                        // Afinal de contas, por que excluir um ticket se ele é finalizado ou cancelado?
                        <BotaoPerigoso
                            confirmation={<div><p><b>ATENÇÃO</b> Se este ticket foi finalizado, <b style={{ color: 'var(--tc-color-danger)', textDecoration: 'underline' }}>NÃO EXCLUA!</b> Entre em Visualizar e marque-o como finalizado!</p><p>Tem certeza que deseja excluir este ticket?</p><p>Esta ação não pode ser desfeita.</p></div>}
                            buttonProps={{ title: !hasAuthorization(user, rolesManager) ? '(Sem permissão)' : 'Excluir' }}
                            variant="none-danger"
                            icon={mdiTrashCan}
                            disabled={!hasAuthorization(user, rolesManager)}
                            onClick={() => {
                                deleteMutator.mutate(row.id);
                            }}
                        /> */}
                    </HBox>
                );
            },
        },
    ];

    const queryList = useInfiniteQuery({
        queryKey: ['tickets', debouncedSearch, statusFilter, departamentoFilter, meusTickets],
        queryFn: async ({ pageParam = 0 }) => {
            const params = new URLSearchParams();
            params.append('skip', pageParam.toString());
            params.append('take', '20');

            if (meusTickets === true) {
                params.append('meus', 'true');
            }

            // Add status filter
            if (statusFilter.length > 0) {
                params.append('status', statusFilter.join(','));
            }

            // Add departamento filter
            if (departamentoFilter) {
                params.append('departamento', departamentoFilter.toString());
            }

            // Add search if present
            if (debouncedSearch) {
                params.append('busca', debouncedSearch);
            }

            const { data } = await axios.get<ResponseList>(`/api/ticket?${params.toString()}`, {
                withCredentials: true,
            });

            return {
                data: {
                    result: data.result,
                    total: data.total,
                    cursor: data.cursor !== null && data.result.length > 0 ? data.cursor : null,
                }
            };
        },
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    const changeMutator = useMutation(
        async () => {
            if (!editObject) return;

            setFieldErrors(null);
            if (editObject.id) {
                return await axios.put(`/api/ticket/${editObject.id}`, editObject, {
                    withCredentials: true,
                });
            } else {
                return await axios.post(`/api/ticket`, editObject, {
                    withCredentials: true,
                });
            }
        },
        {
            onSuccess: () => {
                if (!editObject.id) {
                    toast('Ticket adicionado com sucesso', {
                        toastId: 'add-success',
                        position: 'top-center',
                        type: 'success',
                        autoClose: 5000,
                    });
                } else {
                    toast('Ticket atualizado com sucesso', {
                        toastId: 'edit-success',
                        position: 'top-center',
                        type: 'success',
                        autoClose: 5000,
                    });
                }

                setFieldErrors(null);
                queryList.refetch();
                setEditObject(null);
            },
            onError: (err: any) => {
                if (err.response?.status === 422) {
                    setFieldErrors(err.response?.data?.fieldErrors ?? {});
                }
                toast(err.response?.data?.msg ?? 'Erro desconhecido', {
                    toastId: 'add-error',
                    position: 'top-center',
                    type: 'warning',
                    autoClose: 5000,
                });
            },
        },
    );

    const deleteMutator = useMutation(
        async (id: number) => {
            return await axios.delete(`/api/ticket/${id}`, {
                withCredentials: true,
            });
        },
        {
            onSuccess: () => {
                toast('Ticket apagado com sucesso', {
                    toastId: 'del-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
                queryList.refetch();
            },
            onError: (err: any) => {
                console.error(err);
                toast('Ocorreu um erro ao tentar apagar o ticket.', {
                    toastId: 'del-error',
                    position: 'top-center',
                    type: 'warning',
                    autoClose: 5000,
                });
            },
            onSettled: () => {
                queryList.refetch();
            },
        },
    );

    const handleCancel = useCallback(() => {
        setEditObject(null);
        setFieldErrors(null);
    }, []);

    const handleSave = useCallback(() => {
        changeMutator.mutate()
    }, [changeMutator]);

    const handleFilterStatus = (status: string) => {
        setStatusFilter(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    const handleFilterDepartamento = (departamentoId: string | null) => {
        setDepartamentoFilter(departamentoId ? parseInt(departamentoId, 10) : null);
    };

    const handleAssumirTicket = useCallback((ticket: any) => {
        axios.post(`/api/ticket/${ticket.id}/assumir`, {}, { withCredentials: true })
            .then(() => {
                toast('Ticket assumido com sucesso!', { type: 'success' });
                queryList.refetch();
            })
            .catch((error) => {
                toast(error.response?.data?.msg || 'Erro ao assumir ticket', { type: 'error' });
            });
    }, []);

    // Manipulador para transferência de ticket
    const handleTransferir = useCallback((ticket: any) => {
        setTicketParaTransferir(ticket);
        setShowTransferirModal(true);
    }, []);

    // Mutation para transferência de tickets
    const transferirMutation = useMutation(
        async (data: { ticketId: number, dados: TransferenciaTicketData }) => {
            return await axios.post(
                `/api/ticket/${data.ticketId}/transferir`,
                data.dados,
                { withCredentials: true }
            );
        },
        {
            onSuccess: () => {
                toast('Ticket transferido com sucesso!', { type: 'success' });
                setShowTransferirModal(false);
                setTicketParaTransferir(null);
                queryList.refetch();
            },
            onError: (error: any) => {
                toast(error.response?.data?.msg || 'Erro ao transferir ticket', { type: 'error' });
            }
        }
    );

    const statusOptions = [
        { status: 'aberto', label: 'Aberto', color: 'var(--tc-color-primary)' },
        { status: 'em_progresso', label: 'Em Progresso', color: 'var(--tc-color-warning)' },
        { status: 'finalizado', label: 'Finalizado', color: 'var(--tc-color-success)' },
        { status: 'cancelado', label: 'Cancelado', color: 'var(--tc-color-danger)' },
    ];

    const departamentoOptions = [
        { value: null, label: 'Todos' },
        { value: 'financeiro', label: 'Financeiro' },
        { value: 'tecnologia', label: 'Tecnologia' },
        { value: 'rh', label: 'Recursos Humanos' },
        // Adicione mais departamentos conforme necessário
    ];

    const queryFlatList = queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
    const total = queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap="16px">
                    <HBox className={styles.header}>
                        <div className={styles.headerTitulo}>
                            <Titulo>Tickets de Suporte</Titulo>
                            <Descricao>
                                Gerencie tickets de suporte para seus clientes
                            </Descricao>
                        </div>
                        <div className={styles.headerAcoes}>
                            <Botao
                                variant="primary"
                                icon={mdiPlus}
                                onClick={() => {
                                    setEditObject({
                                        titulo: '',
                                        status: 'aberto',
                                        prioridade: 'media',
                                    });
                                    setFieldErrors(null);
                                }}
                            >
                                Novo Ticket
                            </Botao>
                        </div>
                    </HBox>

                    <HBox gap="8px" style={{ alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <CampoTexto
                                value={search}
                                onChange={setSearch}
                                placeholder="Buscar tickets..."
                                useAlt
                                prefix={<Icon path={mdiMagnify} size={0.8} />}
                                style={{ maxWidth: 300 }}
                            />
                        </div>
                        <div>
                            <HBox gap="8px">
                                <Botao
                                    variant={showFilters ? "primary" : "outline-secondary"}
                                    icon={mdiFilterOutline}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    Filtros
                                </Botao>

                                <Botao
                                    variant="primary"
                                    style={{
                                        borderColor: 'var(--tc-color-gray-300)',
                                        color: meusTickets ? 'white' : 'var(--tc-color-gray-700)',
                                        backgroundColor: meusTickets ? 'var(--tc-color-gray-700)' : 'transparent',
                                    }}
                                    onClick={() => setMeusTickets(!meusTickets)}
                                >
                                    Meus tickets
                                </Botao>


                                {statusOptions.map(option => (
                                    <Botao
                                        key={option.status}
                                        variant={statusFilter.includes(option.status) ? "primary" : "secondary"}
                                        style={{
                                            borderColor: option.color,
                                            color: statusFilter.includes(option.status) ? '#fff' : option.color,
                                            backgroundColor: statusFilter.includes(option.status) ? option.color : 'transparent',
                                        }}
                                        onClick={() => handleFilterStatus(option.status)}
                                    >
                                        {option.label}
                                    </Botao>
                                ))}
                            </HBox>
                        </div>
                    </HBox>

                    {showFilters && (
                        <Grid style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--tc-color-gray-100)', borderRadius: '8px' }}>
                            <GridItem sm={12} md={4}>
                                <CampoSeletor
                                    instanceId="departamentoFilter"
                                    label="Filtrar por Departamento"
                                    value={departamentoFilter?.toString() || null}
                                    onChange={handleFilterDepartamento}
                                    options={[
                                        { id: '', label: 'Todos os departamentos' },
                                        ...(gruposQuery.data?.map((g: any) => ({
                                            id: g.id.toString(),
                                            label: g.nome
                                        })) || [])
                                    ]}
                                    loading={gruposQuery.isLoading}
                                    showSearch
                                    searchFields={['label']}
                                />
                            </GridItem>
                        </Grid>
                    )}

                    {queryList.isFetching && (
                        <div>
                            Aguarde...{' '}
                            <Icon
                                path={mdiLoading}
                                size={0.8}
                                color="var(--tc-color-gray-700)"
                                spin={1}
                            />
                        </div>
                    )}

                    {searchParams.entries().map(([k, v]) => (
                        <Badge key={k} variant="info" style={{ alignSelf: 'flex-start', padding: '4px 8px' }}>
                            <span>{k}={v}</span>
                            <Botao variant="none-info" onClick={() => {
                                if (k === 'id') {
                                    setSelectedTickets([])
                                }

                                searchParams.delete(k);

                                queryList.refetch();

                                const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
                                window.history.replaceState({}, '', newUrl);
                            }}
                                icon={mdiDeleteCircle}
                            />
                        </Badge>
                    ))}

                    {!queryList.isFetching && (
                        <div>
                            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
                            resultados.
                        </div>
                    )}

                    <DataTable
                        isLoading={queryList.isFetching}
                        cabecalhos={headers}
                        linhas={queryFlatList ?? []}
                        hasMoreData={queryList.hasNextPage}
                        onLoadMore={async () => queryList.fetchNextPage()}
                        highlightLine={(row: any) => selectedTickets.includes(row.id)}
                    />
                    {!queryList.isFetching && queryFlatList.length === 0 && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '2rem',
                            }}
                        >
                            Nenhum ticket encontrado
                        </div>
                    )}
                </VBox>
            </div>

            <Modal
                show={!!editObject}
                title={editObject?.id > 0 ? "Editar Ticket" : "Novo Ticket"}
                onClose={handleCancel}
                onCancel={handleCancel}
                onConfirm={handleSave}
            >
                {editObject && (
                    <FormTicket
                        value={editObject}
                        onChange={setEditObject}
                        fieldErrors={fieldErrors}
                    />
                )}
            </Modal>

            {/* Modal de transferência */}
            <ModalTransferirTicket
                show={showTransferirModal}
                onClose={() => {
                    setShowTransferirModal(false);
                    setTicketParaTransferir(null);
                }}
                onTransferir={(dados) => {
                    if (ticketParaTransferir) {
                        transferirMutation.mutate({
                            ticketId: ticketParaTransferir.id,
                            dados
                        });
                    }
                }}
                ticketId={ticketParaTransferir?.id || 0}
            />
            {/* Modal de Detalhes do Ticket */}
            <Modal
                show={ticketDetalheId !== null}
                size="lg"
                title={ticketDetalheId ? `Detalhes do Ticket #${ticketDetalheId}` : 'Detalhes do Ticket'}
                onClose={() => setTicketDetalheId(null)}
                onCancel={() => setTicketDetalheId(null)}
                hideControls
            >
                {ticketDetalheId !== null && (
                    <TicketDetalhe
                        ticketId={ticketDetalheId}
                        onClose={() => setTicketDetalheId(null)}
                        onUpdated={() => {
                            // Após atualizar algo nos detalhes, refetch na lista
                            queryList.refetch();
                        }}
                        onAssumir={() => handleAssumirTicket({ id: ticketDetalheId })}
                    />
                )}
            </Modal>
        </LayoutUsuario>
    );
};

export default TicketPage;
